import { Redirect, router } from 'expo-router';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { StyleSheet, View } from 'react-native';

import { BackChevron, goBack } from '@/components/BackChevron';
import { Button } from '@/components/Button';
import { Heading } from '@/components/Heading';
import { Input } from '@/components/Input';
import { Overline } from '@/components/Overline';
import { Screen } from '@/components/Screen';
import { Text } from '@/components/Text';
import { spacing } from '@/constants/theme';
import { campaignCopy } from '@/lib/campaignCopy';
import { formatUsPhoneDisplay, normalizeUsPhone } from '@/lib/contact';
import { GIFTING_ROUTES, giftingRouteFor } from '@/lib/giftingRoute';
import { isApiError, RateLimitError } from '@/services/errors';
import { authStore } from '@/stores/auth.store';
import { giftingStore } from '@/stores/gifting.store';

const COMPLETE_REDIRECT = '/season-of-gifting/complete';

/**
 * Campaign door: phone first, nothing else.
 *
 * Identity comes before any profile write. Previously this was one long
 * unauthenticated form, which meant an existing athlete typed everything out
 * only for the server to discard it (unauth POSTs must never mutate an
 * account) and then hit the athletes-only wall anyway. Now the PIN resolves
 * who they are first, and `complete.tsx` asks only for what their record is
 * actually missing.
 *
 * The "already have an account" link goes to the generic login, which also
 * accepts email codes — the rescue for a STOP'd number or a legacy
 * email-only account. Deliberately NOT an email toggle on this screen:
 * minting a bare email-only account here creates a loop (the account owns
 * the email, so completing later 409s on its own address).
 *
 * Signed-in visitors never see the form — this was the last campaign screen
 * without that guard, and the omission wasn't cosmetic. (auth)/_layout
 * redirects authed users away from /(auth)/verify, which is where this
 * screen pushes AFTER requestPin: they got a real SMS, then got bounced
 * past the PIN anyway, number unused. Hand-made /start links are in the
 * wild (they predate campaignShareUrl), so each one burned a message.
 */
const StartScreen = observer(() => {
  const [phoneRaw, setPhoneRaw] = useState('');
  const [focused, setFocused] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const phoneE164 = normalizeUsPhone(phoneRaw);

  async function submit() {
    if (!phoneE164 || submitting) return;
    setSubmitting(true);
    setErrorMessage(null);
    try {
      await authStore.requestPin('sms', phoneE164);
      router.push({
        pathname: '/(auth)/verify',
        params: { contact: phoneE164, channel: 'sms', redirect: COMPLETE_REDIRECT },
      });
    } catch (err) {
      if (err instanceof RateLimitError) {
        setErrorMessage(`Too many tries. Try again in ${err.retryAfterSeconds}s.`);
      } else if (isApiError(err)) {
        setErrorMessage(err.message);
      } else {
        setErrorMessage('Could not send your code. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  // Gated on `hydrated` so a cold web load at this URL doesn't decide from a
  // first frame that always looks logged-out. Where an authed user goes is
  // the SERVER's call (gifting_next_step), read through the same helper the
  // index and the landing use — a second opinion here is exactly the drift
  // that produced the /choose <-> index redirect loop. `null` (no step: not
  // an athlete, campaign closed) falls back to the landing, which owns the
  // "for college athletes" state.
  //
  // `user` is checked explicitly even though isAuthenticated already implies
  // it (`Boolean(sessionToken && user)`) — that invariant lives in
  // auth.store.ts, and giftingRouteFor(null) returns THIS route. Redefining
  // isAuthenticated as token-only would make this screen redirect to itself;
  // the extra clause makes it render the form instead.
  if (authStore.hydrated && authStore.isAuthenticated && authStore.user) {
    return <Redirect href={giftingRouteFor(authStore.user) ?? GIFTING_ROUTES.landing} />;
  }

  return (
    // Same reason as complete.tsx: the phone field's CTA sits low enough that
    // the keyboard can cover it on shorter devices.
    <Screen keyboardAvoiding width="column">
      <BackChevron onPress={() => goBack('/season-of-gifting')} />
      <View style={styles.body}>
        {/* Pre-login screen: the catalog response (fetched on the landing
            they came from) is the copy source; fallback covers a cold deep
            link. */}
        <Overline>{campaignCopy(giftingStore.campaignCopy).name}</Overline>
        <Heading size="4xl" style={styles.headline}>
          Start with your number.
        </Heading>
        <Text color="textMuted" style={styles.copy}>
          We&apos;ll text you a code. If you already have a MarketPryce account, this signs you
          straight in.
        </Text>

        <Input
          label="Phone number"
          focused={focused}
          value={formatUsPhoneDisplay(phoneRaw)}
          onChangeText={setPhoneRaw}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder="(555) 123-4567"
          keyboardType="phone-pad"
          autoComplete="tel"
          textContentType="telephoneNumber"
          maxLength={14}
          style={styles.field}
        />

        {errorMessage && (
          <Text variant="caption" color="danger">
            {errorMessage}
          </Text>
        )}

        <Button
          label={submitting ? 'Sending code…' : 'Send my code'}
          onPress={() => void submit()}
          disabled={!phoneE164 || submitting}
          style={styles.cta}
        />

        <Text variant="caption" color="textMuted" align="center" style={styles.consent}>
          By continuing you agree to receive a one-time code via SMS. Standard rates apply. Reply
          STOP to opt out.
        </Text>

        <View style={styles.rescue}>
          <Text variant="caption" color="textMuted">
            Already have an account?
          </Text>
          <Button
            label="Sign in"
            variant="ghost"
            size="sm"
            onPress={() =>
              router.push({
                pathname: '/(auth)/login',
                params: { redirect: COMPLETE_REDIRECT },
              })
            }
          />
        </View>
      </View>
    </Screen>
  );
});

export default StartScreen;

const styles = StyleSheet.create({
  body: {
    paddingTop: spacing.sm,
  },
  headline: {
    marginTop: spacing.md,
  },
  copy: {
    marginTop: spacing.base,
  },
  field: {
    marginTop: spacing.lg,
  },
  cta: {
    marginTop: spacing.lg,
  },
  consent: {
    marginTop: spacing.md,
  },
  rescue: {
    marginTop: spacing.lg,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
  },
});
